import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { Order, OrderStatus, OrderType, PaymentStatus } from '../models/posTypes';

interface POSContextType {
  orders: Order[];
  loadOrders: () => void;
}

const POSContext = createContext<POSContextType | undefined>(undefined);

export const usePOS = (): POSContextType => {
  const context = useContext(POSContext);
  if (!context) {
    throw new Error('usePOS must be used within a POSProvider');
  }
  return context;
};

// Sample mock data
const mockOrders: Order[] = [
  {
    id: '1',
    orderNumber: 'ORD-001',
    createdAt: Date.now() - 3600000, // 1 hour ago
    type: OrderType.RETAIL,
    customerName: 'John Doe',
    subtotal: 29.99,
    discountAmount: 0,
    taxAmount: 2.40,
    tipAmount: 0,
    total: 32.39,
    status: OrderStatus.COMPLETED,
    paymentStatus: PaymentStatus.PAID,
    items: [
      {
        id: '1',
        name: 'Beach Towel',
        price: 19.99,
        quantity: 1,
        modifiers: [],
        notes: ''
      },
      {
        id: '2',
        name: 'Sunscreen',
        price: 10.00,
        quantity: 1,
        modifiers: [],
        notes: ''
      }
    ]
  },
  {
    id: '2',
    orderNumber: 'ORD-002',
    createdAt: Date.now() - 7200000, // 2 hours ago
    type: OrderType.DINE_IN,
    customerName: 'Jane Smith',
    subtotal: 45.50,
    discountAmount: 5.00,
    taxAmount: 3.25,
    tipAmount: 8.00,
    total: 51.75,
    status: OrderStatus.COMPLETED,
    paymentStatus: PaymentStatus.PAID,
    items: [
      {
        id: '1',
        name: 'Cheeseburger',
        price: 12.99,
        quantity: 2,
        modifiers: [
          {
            id: '1',
            name: 'Extras',
            options: [{ id: '1', name: 'Extra Cheese', price: 1.00 }]
          }
        ],
        notes: 'Medium well'
      },
      {
        id: '2',
        name: 'Fries',
        price: 4.99,
        quantity: 2,
        modifiers: [],
        notes: ''
      },
      {
        id: '3',
        name: 'Soft Drink',
        price: 2.99,
        quantity: 2,
        modifiers: [],
        notes: ''
      }
    ]
  },
  {
    id: '3',
    orderNumber: 'ORD-003',
    createdAt: Date.now() - 10800000, // 3 hours ago
    type: OrderType.TAKEOUT,
    customerName: 'Alex Johnson',
    subtotal: 22.99,
    discountAmount: 0,
    taxAmount: 1.84,
    tipAmount: 0,
    total: 24.83,
    status: OrderStatus.OPEN,
    paymentStatus: PaymentStatus.PENDING,
    items: [
      {
        id: '1',
        name: 'Pizza',
        price: 22.99,
        quantity: 1,
        modifiers: [],
        notes: 'Extra crispy'
      }
    ]
  }
];

interface POSProviderProps {
  children: ReactNode;
}

export const POSProvider: React.FC<POSProviderProps> = ({ children }) => {
  const [orders, setOrders] = useState<Order[]>([]);

  const loadOrders = () => {
    // In a real app, this would be an API call
    setOrders(mockOrders);
  };

  useEffect(() => {
    loadOrders();
  }, []);

  return (
    <POSContext.Provider value={{ orders, loadOrders }}>
      {children}
    </POSContext.Provider>
  );
}; 